import React, { useContext } from "react";

import type { ErrorModalButtonType } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { getPurchaseTripOperator } from "@app/helpers/purchase/get-purchase-trip-operator";
import { useModalActions } from "@app/store/hooks/useModalActions";
import { clickedConfirmCancellation } from "@app/tracking/account-tracking";
import type { NapiCancellationOption } from "@app/types/napi-v2-cancellation-types";
import type { CreditType } from "@app/types/napi-v2-request-type";

import { CancelMessage } from "./cancel-message";
import { getRefundMethod } from "./helpers";
import { CancelDialog } from "../cancel-dialog";
import { CancelTripContext } from "../cancel-trip-context";

interface Props {
  cancellation_options: NapiCancellationOption[];
  onConfirm: (refund_method: CreditType) => void;
}

export const CancelTripConfirmation: React.FC<Props> = ({
  cancellation_options,
  onConfirm
}) => {
  const { closeModal } = useModalActions();
  const { tracker, translator } = useAppContext();
  const { purchase } = useContext(CancelTripContext);

  const operator = getPurchaseTripOperator(purchase);

  const onAction = (button_type: ErrorModalButtonType) => {
    if (button_type === "cta") {
      const refund_method = getRefundMethod(cancellation_options);
      tracker.asyncTrack(clickedConfirmCancellation(refund_method));
      onConfirm(refund_method);
    } else {
      closeModal();
    }
  };

  return (
    <CancelDialog
      title={translator.t("!account.trip-details.cancel-refund.title")}
      message={
        <CancelMessage
          operator_name={operator.name}
          cancellation_options={cancellation_options}
        />
      }
      actionHandler={onAction}
      onClose={closeModal}
      cta_button_config={{
        label: translator.t("!account.trip-details.cancel-tickets.title")
      }}
      secondary_button_config={{
        label: translator.t("!account.trip-details.cancel-tickets.not-now")
      }}
    />
  );
};
